// Browser Sentry adapter for the creator app. Mirrors the worker adapter's
// shape: idempotent, dynamic-imports the SDK so a missing DSN or missing
// package is a no-op, never crashes the app at boot.
//
// Set the DSN via Vite env var VITE_SENTRY_DSN at build time (or leave it
// unset for dev / local — the adapter then degrades cleanly).

interface BrowserSentry {
  init?: (opts: Record<string, unknown>) => void;
  captureException?: (err: unknown) => void;
  captureMessage?: (msg: string, level?: string) => void;
}

let initialized = false;
let captureExceptionFn: ((err: unknown) => void) | null = null;
let captureMessageFn: ((msg: string, level?: string) => void) | null = null;

export interface BrowserSentryOptions {
  dsn?: string;
  environment?: string;
  release?: string;
}

export async function initBrowserSentry(opts: BrowserSentryOptions): Promise<void> {
  if (initialized) return;
  initialized = true;
  if (!opts.dsn) return;
  try {
    const sentry = (await import('@sentry/browser')) as BrowserSentry;
    sentry.init?.({
      dsn: opts.dsn,
      environment: opts.environment ?? 'production',
      release: opts.release,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0
    });
    captureExceptionFn = sentry.captureException ?? null;
    captureMessageFn = sentry.captureMessage ?? null;

    window.addEventListener('error', (e) => captureExceptionFn?.(e.error ?? e.message));
    window.addEventListener('unhandledrejection', (e) =>
      captureExceptionFn?.(e.reason ?? new Error('unhandled rejection'))
    );
  } catch {
    /* no-op: package not installed */
  }
}

export function captureBrowserException(err: unknown): void {
  if (captureExceptionFn) {
    try {
      captureExceptionFn(err);
      return;
    } catch {
      /* fall through */
    }
  }
  // eslint-disable-next-line no-console
  console.error('captureException (sentry not wired):', err);
}

export function captureBrowserMessage(msg: string, level: 'warning' | 'error' = 'warning'): void {
  if (captureMessageFn) {
    try {
      captureMessageFn(msg, level);
      return;
    } catch {
      /* fall through */
    }
  }
}

/** Useful for manual smoke-test from devtools: `window.__sentryTest()`. */
export function exposeSentryTestHook(): void {
  if (typeof window !== 'undefined') {
    (window as unknown as { __sentryTest: () => void }).__sentryTest = () => {
      throw new Error('sentry-browser-smoke-test — if you see this in Sentry, the integration is live');
    };
  }
}
